import { SelfContainedCommand, CommandExecutionContext } from '../../../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../../types/standardToolResults';
import { API_BASE_URL } from '../../../../api/config';
import fetchService from '../../../../services/fetchService';

export const automation_run: SelfContainedCommand = {
  id: 'automation_run',
  description: `Run an automation immediately by its ID, regardless of its schedule.

Use automation_list first to get the automationId if you don't have it.

Optionally provide a one-time task override that parameterizes this run without changing the automation permanently.`,
  autonomyLevel: 'auto',
  visualTreatment: 'narrative',

  schema: {
    parameters: [
      {
        name: 'automationId',
        type: 'string',
        description: 'The _id of the automation entity to run',
        required: true,
        examples: ['automation_20240101_123456'],
      },
      {
        name: 'task',
        type: 'string',
        description: 'Optional one-time task override for this run only. Does not permanently change the automation.',
        required: false,
      },
    ],
  },

  examples: {
    good: [
      {
        input: { automationId: 'automation_20240101_123456' },
        expectedOutput: createSuccessResult('Automation started', { jobId: 'job_123', status: 'queued' }),
        description: 'Run an automation immediately',
      },
      {
        input: { automationId: 'automation_20240101_123456', task: 'Focus only on APAC leads this week' },
        expectedOutput: createSuccessResult('Automation started', { jobId: 'job_456', status: 'queued' }),
        description: 'Run with a one-time task override',
      },
    ],
    bad: [
      {
        input: {},
        error: 'automationId is required',
        why: 'Must specify which automation to run',
        fix: 'Use automation_list to find the automation ID first',
      },
    ],
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { automationId, task } = params;
    if (!automationId) return createErrorResult('automationId is required', '', 'validation');

    try {
      const result = await fetchService(`${API_BASE_URL}/automations/${automationId}/run`, {
        method: 'POST',
        ...(task ? { body: JSON.stringify({ task }) } : {}),
      });
      return createSuccessResult(
        `Automation started (job: ${result.jobId})`,
        { jobId: result.jobId, status: result.status, threadId: result.threadId },
      );
    } catch (error: any) {
      return createErrorResult('Failed to run automation', error.message || 'Unknown error', 'execution');
    }
  },
};
